import { api } from './api';

export type BankAccountType = 'operating' | 'trust' | 'other';
export type BankAccountOwnerType = 'chambers' | 'barrister';

export interface BankAccount {
  id: string;
  chambersId: string;
  ownerType: BankAccountOwnerType;
  barristerOwnerId: string | null;
  name: string;
  type: BankAccountType;
  bankName: string | null;
  accountNumberLast4: string | null;
  sortCode: string | null;
  currency: string;
  description: string | null;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  barristerOwner: {
    id: string;
    user: { id: string; firstName: string; lastName: string };
  } | null;
}

export interface ListBankAccountsQuery {
  type?: BankAccountType;
  ownerType?: BankAccountOwnerType;
  barristerOwnerId?: string;
  scope?: 'all' | 'mine' | 'chambers';
  isActive?: 'true' | 'false';
}

export async function fetchBankAccounts(query: ListBankAccountsQuery = {}): Promise<BankAccount[]> {
  const res = await api.get<{ accounts: BankAccount[] }>('/bank-accounts', { params: query });
  return res.data.accounts;
}

export async function fetchBankAccount(id: string): Promise<BankAccount> {
  const res = await api.get<{ account: BankAccount }>(`/bank-accounts/${id}`);
  return res.data.account;
}

export interface CreateBankAccountPayload {
  name: string;
  type: BankAccountType;
  ownerType?: BankAccountOwnerType; // defaults to 'chambers'
  barristerOwnerId?: string | null;
  bankName?: string | null;
  accountNumberLast4?: string | null;
  sortCode?: string | null;
  currency?: string;
  description?: string | null;
  isDefault?: boolean;
}

export async function createBankAccount(payload: CreateBankAccountPayload): Promise<BankAccount> {
  const res = await api.post<{ account: BankAccount }>('/bank-accounts', payload);
  return res.data.account;
}

export interface UpdateBankAccountPayload {
  name?: string;
  bankName?: string | null;
  accountNumberLast4?: string | null;
  sortCode?: string | null;
  currency?: string;
  description?: string | null;
  isDefault?: boolean;
}

export async function updateBankAccount(id: string, payload: UpdateBankAccountPayload): Promise<BankAccount> {
  const res = await api.patch<{ account: BankAccount }>(`/bank-accounts/${id}`, payload);
  return res.data.account;
}

export async function setBankAccountActive(id: string, isActive: boolean): Promise<BankAccount> {
  const res = await api.patch<{ account: BankAccount }>(`/bank-accounts/${id}/active`, { isActive });
  return res.data.account;
}

// Display helper — sort code stored as 6 digits, shown as xx-xx-xx.
export function formatSortCode(raw: string | null): string {
  if (!raw || raw.length !== 6) return '—';
  return `${raw.slice(0, 2)}-${raw.slice(2, 4)}-${raw.slice(4, 6)}`;
}

export function formatAccountMasked(last4: string | null): string {
  if (!last4) return '—';
  return `•••• ${last4}`;
}
